import { useEffect, useRef, useState } from "react";
import { api } from "../../../../services/api/api";
import SafetyDiagImageModal from "./SafetyDiagImageModal";

export interface SafetyDiagMediaImageProps {
  downloadPath: string;
  alt: string;
  className?: string;
  previewable?: boolean;
  modalTitle?: string;
}

/**
 * Renders a safety-diag capture/signature image by streaming its (decrypted) bytes from
 * the API and turning them into a blob URL — the same approach as intervention photos
 * ({@code LazyImage}). Safety-diag media lives in an SSE-C bucket, so a presigned S3 URL
 * can't be opened directly by the browser (the customer encryption key can't ride in the
 * URL); we proxy the bytes through the API instead.
 * When `previewable` is true, clicking the thumbnail opens a full-size modal.
 */
export default function SafetyDiagMediaImage({
  downloadPath,
  alt,
  className,
  previewable = false,
  modalTitle,
}: SafetyDiagMediaImageProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    setUrl(null);
    setFailed(false);

    api
      .getPhotos(downloadPath)
      .then((buffer) => {
        if (!active) return;
        const objectUrl = URL.createObjectURL(new Blob([buffer]));
        urlRef.current = objectUrl;
        setUrl(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => {
      active = false;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [downloadPath]);

  if (failed) return null;
  if (!url) return <div className={`animate-pulse bg-gray-200 ${className ?? ""}`} />;

  if (!previewable) {
    return <img src={url} alt={alt} className={className} />;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsPreviewOpen(true)}
        className="group relative inline-flex items-center justify-center rounded overflow-hidden p-0 border-0 bg-transparent text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        aria-label={modalTitle || alt}
      >
        <img
          src={url}
          alt={alt}
          className={`${className ?? ""} cursor-zoom-in transition-transform duration-200 group-hover:scale-105`}
        />
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none rounded" />
      </button>

      {isPreviewOpen && (
        <SafetyDiagImageModal
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          imageUrl={url}
          alt={alt}
          title={modalTitle}
        />
      )}
    </>
  );
}

/** Build the API path that streams a report capture/signature image by its id. */
export function safetyDiagMediaPath(workOrderId: string, mediaId: string): string {
  return `admin/safety-diag/work-orders/${workOrderId}/media/${mediaId}/download`;
}
